import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Gallery } from "@/components/Gallery";
import { KineticLines } from "@/components/KineticLines";

const CHOICES = [
  {
    key: "residential",
    testid: "projects-pick-residential",
    title: "RESIDENTIAL",
    sub: "Homes we've sided, repaired and transformed",
    position: "38% center",
    image: "https://images.pexels.com/photos/7601153/pexels-photo-7601153.jpeg?auto=compress&cs=tinysrgb&w=1920&q=85",
  },
  {
    key: "commercial",
    testid: "projects-pick-commercial",
    title: "COMMERCIAL",
    sub: "Facades, re-clads and build-outs for contractors & developers",
    image: "https://images.pexels.com/photos/10486073/pexels-photo-10486073.jpeg?auto=compress&cs=tinysrgb&w=1920&q=85",
  },
];

export default function Projects() {
  const [params, setParams] = useSearchParams();
  const category = params.get("type");
  const active = CHOICES.find((c) => c.key === category) || null;

  const pick = (key) => {
    setParams({ type: key });
    setTimeout(() => document.getElementById("projects-gallery")?.scrollIntoView({ behavior: "smooth" }), 60);
  };

  return (
    <div data-testid="projects-page" className="min-h-screen bg-background grain flex flex-col">
      <Header />
      <main className="flex-1 pt-16">
        <section className="px-5 md:px-10 pt-14 md:pt-20 pb-10 max-w-7xl mx-auto text-center">
          <KineticLines
            lines={["OUR WORK"]}
            className="font-display text-6xl md:text-8xl leading-[0.9] tracking-wide"
          />
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-4 text-white/50 text-xs md:text-sm tracking-[0.35em] uppercase"
          >
            Choose a project type to see the photos
          </motion.p>
        </section>

        <section className="px-5 md:px-10 max-w-7xl mx-auto grid md:grid-cols-2 gap-5 pb-14" data-testid="projects-choices">
          {CHOICES.map((c, i) => (
            <button
              key={c.key}
              onClick={() => pick(c.key)}
              data-testid={c.testid}
              className={`group relative overflow-hidden text-left border rounded-2xl transition-colors duration-500 min-h-[220px] md:min-h-[300px] ${active?.key === c.key ? "border-primary" : "border-white/10 hover:border-primary/60"}`}
            >
              <motion.img
                src={c.image}
                alt={c.title}
                style={{ objectPosition: c.position || "center" }}
                initial={{ scale: 1.12, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 1.2, delay: 0.3 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/55 group-hover:bg-black/40 transition-colors duration-500" />
              <div className="relative h-full flex flex-col justify-end p-6 md:p-8">
                <h2 className="font-display text-4xl md:text-5xl tracking-wide flex items-center gap-3">
                  {c.title}
                  <ArrowUpRight className="text-primary w-6 h-6 md:w-8 md:h-8 opacity-0 -translate-x-2 translate-y-2 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-all duration-500" aria-hidden="true" />
                </h2>
                <p className="mt-2 text-white/70 text-sm max-w-xs">{c.sub}</p>
                <div className={`mt-4 h-0.5 bg-primary transition-all duration-700 ${active?.key === c.key ? "w-20" : "w-0 group-hover:w-20"}`} />
              </div>
            </button>
          ))}
        </section>

        {active && (
          <div id="projects-gallery" data-testid="projects-gallery">
            <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center gap-2" data-testid="projects-switch">
              {CHOICES.map((c) => (
                <button
                  key={c.key}
                  onClick={() => pick(c.key)}
                  data-testid={`projects-switch-${c.key}`}
                  className={`px-4 py-2 text-[11px] font-bold uppercase tracking-widest border rounded-lg transition-colors duration-300 ${active.key === c.key ? "bg-primary text-primary-foreground border-primary" : "border-white/20 text-white/60 hover:border-primary hover:text-primary"}`}
                >
                  {c.title}
                </button>
              ))}
              <button
                onClick={() => { setParams({}); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                data-testid="projects-switch-back"
                className="ml-auto text-[11px] font-bold uppercase tracking-widest text-white/40 hover:text-primary transition-colors duration-300"
              >
                Choose again
              </button>
            </div>
            <Gallery category={active.key} />
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
